import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { ToastSettings } from "../../../utils/ToastSettings";
import { FinancialAgentCategoryModel } from "../../../models/Financial/FinancialAgentCategories/FinancialAgentCategoryModel";
import { FinancialAgentCategoriesService } from "../../../services/Financial/FinancialAgentCategories/FinancialAgentCategoriesService";

interface ContextProps {
  links: FinancialAgentCategoryModel[];
  isLoadingLinks: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleListByAgentCategory: (agentCategoryId: number) => Promise<void>;
  handleCreate: (financialId: number, agentCategoryId: number) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
}

export const FinancialAgentCategoriesContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface FinancialAgentCategoriesProviderProps {
  children: ReactNode;
}

export const FinancialAgentCategoriesProvider = ({
  children,
}: FinancialAgentCategoriesProviderProps) => {
  const [links, setLinks] = useState<FinancialAgentCategoryModel[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new FinancialAgentCategoriesService();

  const handleListByAgentCategory = async (agentCategoryId: number) => {
    try {
      setIsLoadingLinks(true);
      const [_Response, _Error] = await service.list({
        withPaginate: false,
        filter: { agent_category_id: agentCategoryId },
      });
      setIsLoadingLinks(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        setLinks([]);
        return;
      }

      setLinks(_Response?.items ?? []);
      setError("");
    } catch (e) {
      setIsLoadingLinks(false);
      setLinks([]);
      setError("Não foi possível carregar os vínculos da categoria.");
    }
  };

  const handleCreate = async (financialId: number, agentCategoryId: number) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.create({
        financial_id: financialId,
        agent_category_id: agentCategoryId,
      });

      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      return true;
    } catch (e) {
      setError("Houve um erro ao vincular a categoria.");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const [_Response, _Error] = await service.delete(id);

      if (!!_Error) {
        ToastSettings(_Response?.message || _Error, "bottom-center", "error");
        return false;
      }

      return true;
    } catch (e) {
      setError("Houve um erro ao desvincular a categoria.");
      return false;
    }
  };

  return (
    <FinancialAgentCategoriesContext.Provider
      value={{
        links,
        isLoadingLinks,
        error,
        setError,
        handleListByAgentCategory,
        handleCreate,
        handleDelete,
      }}
    >
      {children}
    </FinancialAgentCategoriesContext.Provider>
  );
};

export const useFinancialAgentCategories = () =>
  useContext(FinancialAgentCategoriesContext);
